import { useGetFieldSitesQuery, useListPtoProjectsQuery } from '@shared/api';
import { useAgentPermission } from '@entities/user';
import { Select } from '@shared/ui/Input';

/** Объект заявки: проект ПТО или площадка прораба (сервер возьмёт её объект). */
export interface SupplyObject {
  projectId: string;
  siteId: string;
}

/**
 * Что выбирает человек: объект ПТО (есть доступ к ПТО) или свою площадку
 * (прораб без доступа к ПТО) — от этого зависит подпись поля.
 */
export function useSupplyObjectKind(): 'project' | 'site' {
  const pto = useAgentPermission('pto');
  const field = useAgentPermission('field');
  return !pto.can_read && field.can_read ? 'site' : 'project';
}

interface Props {
  value: SupplyObject;
  onChange: (v: SupplyObject) => void;
  id?: string;
}

/**
 * Куда нужны материалы. Инженеру ПТО — список объектов ПТО; прорабу без
 * доступа к ПТО — его площадки (объект площадки сервер подставит сам).
 */
export function SupplyObjectSelect({ value, onChange, id }: Props) {
  const pto = useAgentPermission('pto');
  const bySite = useSupplyObjectKind() === 'site';
  const { data: projects } = useListPtoProjectsQuery({ limit: 200 }, { skip: !pto.can_read });
  const { data: sites } = useGetFieldSitesQuery(undefined, { skip: !bySite });

  if (bySite) {
    return (
      <Select
        id={id}
        value={value.siteId}
        onChange={(e) => onChange({ projectId: '', siteId: e.target.value })}
      >
        <option value="">— выберите площадку —</option>
        {(sites?.sites ?? []).map((s) => (
          <option key={s.id} value={s.id}>
            {s.name}
          </option>
        ))}
      </Select>
    );
  }

  return (
    <Select
      id={id}
      value={value.projectId}
      onChange={(e) => onChange({ projectId: e.target.value, siteId: '' })}
    >
      <option value="">— выберите объект —</option>
      {(projects?.items ?? []).map((p) => (
        <option key={p.id} value={p.id}>
          {p.name}
        </option>
      ))}
    </Select>
  );
}
